
import React, { useState } from 'react';
import { Calendar } from "@/components/ui/calendar";
import { addDays, format, isSameDay, getDay, isAfter, isBefore } from "date-fns";
import { Card, CardContent } from "@/components/ui/card";
import { ServiceZone } from '@/types';

interface ScheduleCalendarProps {
  selectedDate: Date | undefined;
  onSelectDate: (date: Date | undefined) => void;
  zone?: ServiceZone;
}

const ScheduleCalendar: React.FC<ScheduleCalendarProps> = ({ 
  selectedDate, 
  onSelectDate,
  zone = ServiceZone.PalmSprings // Default to Palm Springs if not specified
}) => {
  // Get days that are available based on zone
  const getAvailableDays = () => {
    const today = new Date();
    const days: Date[] = [];
    
    // Generate the next 30 days
    for (let i = 1; i <= 30; i++) {
      const day = addDays(today, i);
      const dayOfWeek = getDay(day); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
      
      // Never allow Tuesdays (closed)
      if (dayOfWeek === 2) continue;
      
      // Zone-specific scheduling
      if (zone === ServiceZone.PalmSprings) {
        // Palm Springs: Monday, Wednesday, Friday
        if (dayOfWeek === 1 || dayOfWeek === 3 || dayOfWeek === 5) {
          // Friday at 2PM is reserved for a standing appointment
          if (dayOfWeek === 5 && i % 14 === 0) {
            // Skip this Friday if it's a biweekly standing appointment
            // (every other Friday)
            continue;
          }
          days.push(day);
        }
      } else if (zone === ServiceZone.CathedralCity) {
        // Cathedral City: Thursday, Saturday, Sunday
        if (dayOfWeek === 0 || dayOfWeek === 4 || dayOfWeek === 6) {
          days.push(day);
        }
      } else {
        // Other zones: Any day except Tuesday
        if (dayOfWeek !== 2) {
          days.push(day);
        }
      }
    }
    
    return days;
  };

  return (
    <Card className="border-gray-200 bg-white shadow-sm">
      <CardContent className="p-6">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={onSelectDate}
          disabled={(date) => {
            // Disable dates that are not in the available days list
            return !getAvailableDays().some(d => isSameDay(d, date));
          }}
          modifiers={{
            available: getAvailableDays(),
          }}
          modifiersStyles={{
            available: { 
              fontWeight: "600", 
              backgroundColor: "rgba(0, 0, 0, 0.05)",
              color: "black"
            },
          }}
          className="rounded-md border-gray-200 font-cousine"
        />
      </CardContent>
    </Card>
  );
};

export default ScheduleCalendar;
